"use client"

import { useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

// API endpoints
export const API_ENDPOINTS = {
  // Main API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here
    USERS: "/users",
    // ...
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Add specific endpoints here
    SETTINGS: "/settings",
    // ...
  },
  // Workflow API
  WORKFLOW: {
    BASE_URL: env.workflowApiUrl,
    // Add specific endpoints here
    // ...
  },
  // FBO Report API
  FBO_REPORT: {
    BASE_URL: env.fboReportApiUrl,
    // Add specific endpoints here
    REPORT_PEOPLE: "/report/33",
    // ...
  },
}

// Custom hook for making authenticated API requests
export function useApi() {
  const auth = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = useMemo(() => 
    async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const currentToken = auth.token || tokenManager.getLastToken()
    const headers = new Headers(options.headers)

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    // Add authorization header if token exists
    if (currentToken) {
      headers.set("Authorization", `Bearer ${currentToken}`)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized from", finalUrl)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new Error(errorData.message || `API request failed with status ${retriedResponse.status}`)
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }, [auth.token])

  // Helper methods for common HTTP methods
  const get = useMemo(() => 
    <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "GET" })
  , [fetchWithAuth])

  const post = useMemo(() =>
    <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, {
        ...options,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        body: JSON.stringify(data),
      })
  , [fetchWithAuth])

  const put = useMemo(() =>
    <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, {
        ...options,
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        body: JSON.stringify(data),
      })
  , [fetchWithAuth])

  const del = useMemo(() =>
    <T,>(endpoint: string, options: RequestInit = {}) =>
      fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })
  , [fetchWithAuth])

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods
  const mainApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const adminApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const workflowApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.WORKFLOW.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  const fboReportApi = useMemo(() => ({
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.FBO_REPORT.BASE_URL, path), options),
  }), [auth.token, get, post, put, del])

  return {
    mainApi,
    adminApi,
    workflowApi,
    fboReportApi,
    fetchWithAuth,
  }
} 